import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../Components/Loader";
import "../../Styles/LeaveRequest.css";

function LeaveRequest() {
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState(null);
  const [remarks, setRemarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const formatDate = (dateString) => {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-GB", options);
  };

  const calculateTotalDays = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDifference = end - start;
    const daysDifference = timeDifference / (1000 * 3600 * 24);
    return daysDifference;
  };

  const fetchLeaveRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("No authentication token found. Please log in.");
        throw new Error("No authentication token found");
      }
      const facultyResponse = await axios.get(
        "https://api.uniums.com/api/faculty/admin-leave-requests",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const studentResponse = await axios.get(
        "https://api.uniums.com/api/student/admin-leave-requests-student",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const allRequests = [
        ...facultyResponse.data.data.map(req => ({ ...req, type: "faculty" })),
        ...studentResponse.data.data.map(req => ({ ...req, type: "student" })),
      ];
      const pendingRequests = allRequests.filter((req) => req.status === "Pending");

      setRequests(pendingRequests);
      const initialRemarks = {};
      pendingRequests.forEach((req) => {
        initialRemarks[req._id] = req.adminComment || "";
      });
      setRemarks(initialRemarks);
    } catch (err) {
      console.error("Error fetching leave requests:", err);
      toast.error("Failed to fetch leave requests. Please try again later.");
      setError("Failed to fetch leave requests. Please try again later.");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchLeaveRequests();
  }, []);

  const openConfirmModal = (id, action, remark, type) => {
    setSelectedRequest({ id, remark, type });
    setSelectedAction(action);
    setShowConfirmModal(true);
  };


  const handleAction = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No authentication token found");
      }

      const { id, remark, type } = selectedRequest;
      const endpoint =
        type === "student"
          ? `https://api.uniums.com/api/student/admin-leave-requests-student/${id}`
          : `https://api.uniums.com/api/faculty/admin-leave-requests/${id}`;

      const response = await axios.patch(
        endpoint,
        {
          status: selectedAction,
          adminComment: remark,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setShowConfirmModal(false);
      fetchLeaveRequests();
    } catch (err) {
      console.error("Error updating leave application:", err);
      toast.error("Failed to update leave application. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handleRemarkChange = (id, value) => {
    setRemarks((prev) => ({ ...prev, [id]: value }));
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="leave-management">
      <ToastContainer position="top-right" autoClose={3000} />
      <h1>Leave Management</h1>
      {/* {error && <p className="error">{error}</p>} */}
      <div className="admin-table-container border_top">
        <div className="faculty-table leave_table_td">
          <table>
            <thead>
              <tr className="header_sticky">
                <th className="text_nowrap">SL No</th>
                <th className="text_nowrap">Name</th>
                <th className="text_nowrap">Status</th>
                <th className="text_nowrap">User Code</th>
                <th className="region_th">Region</th>
                <th className="text_nowrap">Leave Type</th>
                <th className="text_nowrap">From Date</th>
                <th className="text_nowrap">To Date</th>
                <th className="text_nowrap">Total Days</th>
                <th className="region_th">Remarks</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {requests.length > 0 ? (
                requests.map((request, index) => (
                  <tr key={request._id}>
                    <td className="text_nowrap">{index + 1}</td>
                    <td className="text_nowrap">{request.name || "N/A"}</td>
                    <td>{request.status || "Pending"}</td>
                    <td className="text_nowrap">{request.employeeCode || "N/A"}</td>
                    <td>{request.reason || "N/A"}</td>
                    <td>{request.leaveType || "N/A"}</td>
                    <td>{formatDate(request.startDate) || "N/A"}</td>
                    <td>{formatDate(request.endDate) || "N/A"}</td>
                    <td className="text_nowrap">{calculateTotalDays(request.startDate, request.endDate)}</td>
                    <td>
                      <textarea
                        className="remarks"
                        rows="2"
                        placeholder="Add remarks here..."
                        value={remarks[request._id]}
                        onChange={(e) => handleRemarkChange(request._id, e.target.value)}
                      ></textarea>
                    </td>
                    <td>
                      <button
                        className="approve-btn"
                        disabled={!remarks[request._id]}
                        onClick={() => openConfirmModal(request._id, "Approved", remarks[request._id], request.type)}
                      >
                        Approve
                      </button>
                      <button
                        className="reject-btn"
                        disabled={!remarks[request._id]}
                        onClick={() => openConfirmModal(request._id, "Rejected", remarks[request._id], request.type)}
                      >
                        Reject
                      </button>

                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="11" className="error" style={{textAlign:'center'}}>No pending leave requests available.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal_overlay_confirm">
          <div className="modal_content_confirm">
            <h2>Confirm Action</h2>
            <p>Are you sure you want to {selectedAction.toLowerCase()} this leave request?</p>
            <div className="modal_buttons_confirm">
              <button
                className={selectedAction === "Approved" ? "approve-btn" : "reject-btn"}
                onClick={handleAction}

              >
                {selectedAction}
              </button>
              <button
                className="cancel_btn_confirm"
                onClick={() => setShowConfirmModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeaveRequest;